import { Modal } from './Modal';
import type { ReactNode } from 'react';
import type { ReactElement } from 'react';

type ConfirmModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: () => void;
  title: string;
  message: string;
  ariaLabel: string;
  isSubmitting: boolean;
  isSubmitDisabled?: boolean;
  submitLabel?: string;
  submittingLabel?: string;
  errorMessage?: string | null;
  children?: ReactNode;
};

export function ConfirmModal({
  isOpen,
  onClose,
  onSubmit,
  title,
  message,
  ariaLabel,
  isSubmitting,
  isSubmitDisabled = false,
  submitLabel = 'Confirm',
  submittingLabel = 'Submitting...',
  errorMessage = null,
  children,
}: ConfirmModalProps): ReactElement {
  function handleSubmit(event: React.FormEvent<HTMLFormElement>): void {
    event.preventDefault();

    if (isSubmitDisabled) {
      return;
    }

    onSubmit();
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} ariaLabel={ariaLabel}>
      <form onSubmit={handleSubmit}>
        <h4 className="text-sm font-semibold text-slate-900">{title}</h4>
        <p className="mt-2 text-sm text-slate-700">{message}</p>
        {errorMessage ? <p className="mt-2 text-sm text-rose-700">{errorMessage}</p> : null}
        {children ? <div className="mt-3">{children}</div> : null}

        <div className="mt-6 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className="rounded-md border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSubmitDisabled}
            className="rounded-md bg-sky-600 px-4 py-2 text-sm font-medium text-white hover:bg-sky-700 disabled:cursor-not-allowed disabled:bg-slate-400"
          >
            {isSubmitting ? submittingLabel : submitLabel}
          </button>
        </div>
      </form>
    </Modal>
  );
}
